import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Alert,
  CircularProgress,
  TablePagination,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import API_BASE_URL from '../config';

const AdminView = () => {
  const [wheels, setWheels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [wheelToDelete, setWheelToDelete] = useState(null);
  const [adminRole, setAdminRole] = useState(null);

  const fetchWheels = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setIsAuthenticated(false);
      setError('Authentication required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/admin/wheels`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      // Check for token refresh
      const newToken = response.headers.get('X-New-Token');
      if (newToken) {
        localStorage.setItem('adminToken', newToken);
      }

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        console.error('Failed to parse response:', e);
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setIsAuthenticated(false);
          localStorage.removeItem('adminToken');
          throw new Error(data?.error || 'Authentication required');
        }
        throw new Error(data?.error || 'Failed to fetch life wheels');
      }

      if (!Array.isArray(data)) {
        console.error('Invalid data format:', data);
        throw new Error('Invalid data format received');
      }

      setWheels(data);
    } catch (error) {
      console.error('Fetch error:', error);
      setError(error.message);
      if (error.message.includes('Authentication')) {
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminProfile = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setIsAuthenticated(false);
      setError('Authentication required');
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const newToken = response.headers.get('X-New-Token');
      if (newToken) {
        localStorage.setItem('adminToken', newToken);
      }

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setIsAuthenticated(false);
          localStorage.removeItem('adminToken');
        }
        throw new Error(data?.error || 'Failed to fetch admin profile');
      }

      const role = data?.role || 'admin';
      setAdminRole(role);
      localStorage.setItem('adminRole', role);
      window.dispatchEvent(new Event('admin-role-changed'));
      return true;
    } catch (profileError) {
      console.error('Admin profile error:', profileError);
      setError(profileError.message);
      localStorage.removeItem('adminRole');
      window.dispatchEvent(new Event('admin-role-changed'));
      return false;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const loginResponse = await fetch(`${API_BASE_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      let data;
      try {
        const text = await loginResponse.text();
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        console.error('Failed to parse response:', e);
        throw new Error('Invalid response from server');
      }

      if (!loginResponse.ok) {
        throw new Error(data?.error || 'Invalid credentials');
      }

      if (!data || !data.token) {
        throw new Error('Invalid response from server');
      }

      localStorage.setItem('adminToken', data.token);
      setIsAuthenticated(true);
      setEmail('');
      setPassword('');
      window.dispatchEvent(new Event('admin-auth-changed'));

      // Fetch wheels immediately
      await fetchAdminProfile();
      await fetchWheels();
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
      setIsAuthenticated(false);
      localStorage.removeItem('adminToken');
      window.dispatchEvent(new Event('admin-auth-changed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setIsAuthenticated(false);
        setAuthChecking(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/me`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        // Check for token refresh
        const newToken = response.headers.get('X-New-Token');
        if (newToken) {
          localStorage.setItem('adminToken', newToken);
        }

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.error || 'Authentication failed');
        }

        setIsAuthenticated(true);
        const role = data?.role || 'admin';
        setAdminRole(role);
        localStorage.setItem('adminRole', role);
        window.dispatchEvent(new Event('admin-role-changed'));
        await fetchWheels();
      } catch (error) {
        console.error('Auth check failed:', error);
        setError(error.message);
        setIsAuthenticated(false);
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminRole');
        window.dispatchEvent(new Event('admin-auth-changed'));
        window.dispatchEvent(new Event('admin-role-changed'));
      } finally {
        setAuthChecking(false);
      }
    };
    checkAuth();
  }, []);

  if (authChecking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box
        component="form"
        onSubmit={handleLogin}
        sx={{
          maxWidth: 400,
          mx: 'auto',
          mt: 4,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Typography variant="h5" gutterBottom>
          Admin Access
        </Typography>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2 }}
          autoComplete="off"
          inputProps={{
            autoComplete: "off",
            form: {
              autoComplete: "off",
            }
          }}
        />
        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!error}
          helperText={error}
          sx={{ mb: 2 }}
          autoComplete="new-password"
          inputProps={{
            autoComplete: "new-password",
            form: {
              autoComplete: "off",
            }
          }}
        />
        <Button
          fullWidth
          variant="contained"
          type="submit"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Login'}
        </Button>
      </Box>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setEmail('');
    setPassword('');
    setWheels([]);
    setAdminRole(null);
    localStorage.removeItem('adminRole');
    window.dispatchEvent(new Event('admin-role-changed'));
  };

  const handleDelete = async (wheel) => {
    setWheelToDelete(wheel);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!wheelToDelete) return;

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/wheels/${wheelToDelete.wheelId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete life wheel');
      }

      // Remove the deleted wheel from the state
      setWheels(wheels.filter(w => w.wheelId !== wheelToDelete.wheelId));
      setDeleteDialogOpen(false);
      setWheelToDelete(null);
    } catch (error) {
      console.error('Delete error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setWheelToDelete(null);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Life Wheels Admin Panel
          </Typography>
          <TextField
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            size="small"
            sx={{ minWidth: { xs: '100%', sm: 280 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
        )}

        {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
        ) : wheels.length === 0 ? (
            <Box sx={{ textAlign: 'center', my: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No life wheels found. They will appear here once users create them.
              </Typography>
            </Box>
        ) : (
            <Box sx={{ overflowX: 'auto', width: '100%', boxShadow: 1, borderRadius: 1 }}>
              <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 200px)' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Score</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wheels
                        .filter((wheel) => {
                          if (!wheel) return false;
                          const term = searchTerm.toLowerCase();
                          if (!term) return true;
                          const name = wheel.name ? wheel.name.toLowerCase() : '';
                          const emailValue = wheel.email ? wheel.email.toLowerCase() : '';
                          return name.includes(term) || emailValue.includes(term);
                        })
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((wheel) => wheel && (
                            <TableRow
                                key={wheel.wheelId}
                                sx={{
                                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                }}
                            >
                              <TableCell>{wheel.name || 'N/A'}</TableCell>
                              <TableCell>{wheel.email || 'N/A'}</TableCell>
                              <TableCell>{wheel.createdAt ? formatDate(wheel.createdAt) : 'N/A'}</TableCell>
                              <TableCell align="right">
                                <Box
                                    component="span"
                                    sx={{
                                      backgroundColor: wheel.averageScore >= 70 ? '#e8f5e9' :
                                          wheel.averageScore >= 40 ? '#fff3e0' :
                                              '#ffebee',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontWeight: 'medium'
                                    }}
                                >
                                  {typeof wheel.averageScore === 'number' ? `${wheel.averageScore}%` : 'N/A'}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                  <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => {
                                        if (wheel.wheelId) {
                                          const basePath = process.env.PUBLIC_URL || '';
                                          window.open(`${basePath}/view/${wheel.wheelId}`, '_blank', 'noopener,noreferrer');
                                        }
                                      }}
                                      disabled={!wheel.wheelId}
                                  >
                                    View
                                  </Button>
                                  <Button
                                      variant="outlined"
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(wheel);
                                      }}
                                      disabled={!wheel.wheelId}
                                  >
                                    Delete
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                  component="div"
                  count={wheels.filter((wheel) => {
                    if (!wheel) return false;
                    const term = searchTerm.toLowerCase();
                    if (!term) return true;
                    const name = wheel.name ? wheel.name.toLowerCase() : '';
                    const emailValue = wheel.email ? wheel.email.toLowerCase() : '';
                    return name.includes(term) || emailValue.includes(term);
                  }).length}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
              />
            </Box>
        )}

        <Dialog
            open={deleteDialogOpen}
            onClose={cancelDelete}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Confirm Deletion
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete the life wheel for {wheelToDelete?.name}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelDelete} color="primary">
              Cancel
            </Button>
            <Button onClick={confirmDelete} color="error" variant="contained" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
  );
};

export default AdminView;
